import { useMemo, useState } from 'react';
import { orderBy } from 'lodash-es';
import { TmdxAvatar } from '@ds';
import { DropdownTypeEnum, FilterDropdown } from '@ui';
import { OrSettingsType, RSSettingsType, SuggestedSettingsEnum } from '@lp/features/RoutePlanner/types';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { formatHomeLocation } from '@lp/features/AssignmentTables/SurgeonsTable/utils';
import { useSurgeonsForAssignment } from '@/queries/resource';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { AssignmentCandidateLocation, CandidateMatchType, CaseMatchType } from '@/api/apis/resources';
import { EmployeeLocation } from '@/api/types';
import { getFullName } from '@/utils/helpers';
import { SuggestedSelectTrigger } from './SuggestedSelectTrigger';
import { getRSSettingsText } from './utils';

const toCandidateLocation = (location?: EmployeeLocation | null): AssignmentCandidateLocation | undefined => {
  if (!location) return undefined;

  const { id, state, ...rest } = location;

  return { ...rest, locationId: id, state: state || '' } as AssignmentCandidateLocation;
};

type RSDropdownProps = {
  data: RSSettingsType;
  orTimeSettings: OrSettingsType;
  handleSettingsChange: (data: RSSettingsType) => void;
};

export const RSDropdown = ({ data, orTimeSettings, handleSettingsChange }: RSDropdownProps) => {
  const { caseId, donorType, organType } = useBaseCaseDetails();
  const { Surgeon } = useRouteResources();
  const { data: rsCandidates, isFetching: isRSCandidatesFetching } = useSurgeonsForAssignment({
    caseId,
    donor: donorType,
    organ: organType,
    orTime: orTimeSettings.value.selectedOr.toAbsoluteString(),
  });
  const [searchValue, setSearchValue] = useState('');

  const { nonEuCountryCodes } = useCountriesData();

  const candidatesById = useMemo(() => new Map((rsCandidates || []).map((rs) => [rs.employeeId, rs])), [rsCandidates]);

  const rsList = useMemo(() => {
    if (!rsCandidates) return [];

    const rsOnCaseList = Surgeon.map((rs) => {
      const employeeFullName = getFullName(rs.data?.firstName, rs.data?.lastName) || '';
      const location = candidatesById.get(rs.resourceId)?.homeLocation ?? toCandidateLocation(rs.data?.homeLocation);

      return {
        name: employeeFullName,
        value: rs.resourceId,
        section: 'On current case',
        data: {
          employeeId: rs.resourceId,
          employeeFullName,
          employeeProfilePhoto: rs.data?.profilePhoto || '',
          location,
        },
      };
    });

    const otherRs = rsCandidates
      .filter((rs) => {
        const isRsOnCase = rsOnCaseList.find((i) => i.value === rs.employeeId);

        if (isRsOnCase) return false;

        if (rs.match.caseType !== CaseMatchType.AssignmentCandidate) return false;
        if (rs.match.matchType === CandidateMatchType.Miss) return false;

        return !!rs.homeLocation?.country && nonEuCountryCodes.has(rs.homeLocation.country);
      })
      .map((rs) => ({
        value: rs.employeeId,
        name: rs.employeeFullName,
        section: 'Other',
        data: {
          employeeId: rs.employeeId,
          employeeFullName: rs.employeeFullName,
          employeeProfilePhoto: rs.employeeProfilePhoto,
          location: rs.homeLocation,
        },
      }));

    return [...rsOnCaseList, ...orderBy(otherRs, (rs) => rs.name.toLowerCase(), 'asc')];
  }, [Surgeon, rsCandidates, candidatesById, nonEuCountryCodes]);

  const rsSearchList = useMemo(() => {
    if (searchValue) {
      return rsList.filter((rs) => {
        return rs.name.toLowerCase().includes(searchValue.toLowerCase());
      });
    }

    return rsList;
  }, [rsList, searchValue]);

  return (
    <FilterDropdown
      title="RS"
      placement="top-start"
      items={rsSearchList}
      type={DropdownTypeEnum.Sections}
      isLoading={isRSCandidatesFetching}
      selectedKeys={data.value?.employeeId ? [data.value.employeeId] : []}
      renderCustomTrigger={(isOpen) => (
        <SuggestedSelectTrigger title="RS:" value={getRSSettingsText(data.value)} isOpen={isOpen} />
      )}
      renderListBoxItem={(item) => {
        const homeLocation = formatHomeLocation(item.data.location);

        return (
          <div className="flex items-center gap-2">
            <TmdxAvatar src={item.data.employeeProfilePhoto} size="lg" radius="lg" name={item.data.employeeFullName} />
            <div className="flex-1 max-w-[220px]">
              <div className="text-m-m text-wrap">{item.data.employeeFullName}</div>
              {!!homeLocation && <div className="text-s-r text-base-20 text-wrap">{homeLocation}</div>}
            </div>
          </div>
        );
      }}
      mode="single"
      onSearchValue={setSearchValue}
      onSelectedKeysChange={(value) => {
        const selectedValue = rsSearchList.find((rs) => rs.value === value[0]);

        handleSettingsChange({
          type: SuggestedSettingsEnum.RS,
          value: selectedValue ? selectedValue.data : null,
        });
      }}
    />
  );
};
